<script>
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { componentStore } from "@common/componentStore/componentStore";
import { library } from "@common/library/library";
import { actionStore } from "@common/actionStore";
import { workflowStore } from "@common/activityStepPanel/workflowStore";

// quicklink: http://localhost/?quicklink=1288ad7f2f73ed36fc721fc1706f4b4e

// Dynamic Surveys
// http://localhost:1024/?quicklink=187de4e7dbe50bf6d05d1d6d5c34f46f#/

const editorSettings = {
    workflowMode: {
        name: 'workflowMode',
        label: 'Type of Workflow',
        type: 'dropdown',
        items: [
            {
                text: 'Organization Workflow',
                value: 'org'
            },
            {
                text: 'Opportunity Workflow',
                value: 'opp'
            }
        ],
        default: 'org'
    },
    workflowEditMode: {
        name: 'workflowEditMode',
        label: 'Edit Mode',
        type: 'dropdown',
        items: [
            {
                text: 'Standard',
                value: 'standard'
            },
            {
                text: 'Opp Edit Mode',
                value: 'oppEdit'
            }
        ],
        default: 'standard'
    },
};

const workflowEditorConfig = {
    cId: "workflowEditor",
    cType: "workflowEditor",
    cTitle: "Workflow Editor",
    cTemplate: `<div class="workflow-editor" :class="{ 'opp-edit-mode': config.workflowEditMode === 'oppEdit' }" v-if="!ddLoading">
        <div class="action-area">
            <control :display="config.workflowEditMode !== 'oppEdit'" :name="'WORKFLOW_LIST_' + workflowModeUC" :value="selectedWorkflow" v-if="!isWfListReloading"></control>
        </div>
        <div v-if="!controls?.['WORKFLOW_LIST_' + workflowModeUC]?.value">
            <p class="workflow-message">
                1. Select a workflow from the list above, or create a <control name="NEW_WORKFLOW" class="inline-link-button"></control> to begin.<br/>
                2. After selecting a workflow, add an activity to start building your workflow.
            </p>
            <div style="text-align: center;">
                <control name="NEW_WORKFLOW" style="display: inline-block;"></control>
            </div>
        </div>
        <div class="selected-workflow-container" v-if="controls?.['WORKFLOW_LIST_' + workflowModeUC]?.value">
            <div class="action-area" v-if="config.workflowEditMode !== 'oppEdit'">
                <control name="SAVE_WORKFLOW" style="margin-top: auto;"></control>
            </div>
            <tab-list cId="tabList_workflowEditor" :prop-tabs-list="editorTabs" :prop-selected-tab="selectedTab"  @update:selectedTab="changeSelectedTab">
                <template #tabList_item_0>
                    <div v-if="selectedTab === 0">
                        <div class="top-actions-panel">
                            <control :name="'ACTIVITY_LIST_' + workflowModeUC"></control>
                            <control name="ADD_ACTIVITY" style="align-self: flex-end; margin: 4px 15px 4px 4px;"></control>
                            <control name="NEW_ACTIVITY" style="align-self: flex-end; margin: 4px 15px 4px 4px;"></control>
                            <control name="TOGGLE_VISIBILITY" style="justify-content: flex-end; text-align: right;" :value="editorVisible" @update:value="editorVisible = $event;"></control>
                        </div>
                        <item-list
                            v-if="computedWorkflowSettings.activities?.length"
                            c-id="itemList_workflow_editor"
                            c-type="itemList"
                            :context-vol-id="volId"
                            :items-list="computedWorkflowSettings.activities"
                            :relative-records="[{}]"
                            :prop-editor-visible="editorVisible"
                            @update:targetedItem="selectActivity"
                            @update:itemOrder="updateOrder"
                            @ready="onStepReady"
                        ></item-list>
                    </div>
                </template>
                <template #tabList_item_1>
                    <div>
                        <control name="WORKFLOW_TITLE" :value="workflowSettings.WORKFLOW_TITLE" @update:value="workflowSettings.WORKFLOW_TITLE = $event;"></control>
                        <control name="WORKFLOW_DESC" :value="workflowSettings.WORKFLOW_DESC" @update:value="workflowSettings.WORKFLOW_DESC = $event;"></control>
                        <control name="WORKFLOW_DEFAULT_STATUS" :value="workflowSettings.WORKFLOW_DEFAULT_STATUS" @update:value="workflowSettings.WORKFLOW_DEFAULT_STATUS = $event;"></control>
                        <control name="WORKFLOW_DISABLE_AUTO_STATUS_UPDATES" class="disable-auto-status-updates" :value="workflowSettings.WORKFLOW_DISABLE_AUTO_STATUS_UPDATES" @update:value="workflowSettings.WORKFLOW_DISABLE_AUTO_STATUS_UPDATES = $event;"></control>
                        <control v-if="controls?.['WORKFLOW_LIST_' + workflowModeUC]?.value > 0" name="WORKFLOW_DELETE"></control>
                    </div>
                </template>
                <template #tabList_item_2>
                    <div>
                        <div class="add-workflow-controls">
                            <control :name="'ADD_USAGE_' + workflowModeUC" class="add-org-dropdown"></control>
                            <control :name="'ADD_USAGE_BUTTON_' + workflowModeUC"></control>
                        </div>
                        <control :name="'USAGE_' + this.workflowModeUC"></control>
                    </div>
                </template>
                <template v-for="(tab, index) in editorTabs.filter((iTab) => iTab.oppWorkflowIndex)"  #[tab.slotTemplateId] :key="tab.oppWorkflowIndex">
                    <p>This tab is for opp level editing. To edit this workflow in more detail, use the admin settings editor.</p>
                    <item-list
                        v-if="computedWorkflowSettings?.activities?.length && selectedTab === index + 3"
                        :c-id="'itemList_workflow_editor_oppView__' + index"
                        c-type="itemList"
                        :context-vol-id="volId"
                        :context-opp-id="oppId"
                        :items-list="computedWorkflowSettings.activities"
                        :relative-records="[{}]"
                        :prop-editor-visible="editorVisible"
                        @update:targetedItem="selectActivity"
                        @ready="onStepReady"
                        @openDynamicSurveys="openDynamicSurveysDialog"
                    ></item-list>
                </template>
            </tab-list>
        </div>
        <div v-for="(activity, aIndex) in computedWorkflowSettings.activities" :key="activity.aIndex + '_' + computedWorkflowSettings.workflow?.workflowId">
            <teleport v-if="editorControlsTeleportIds[activity.ACTIVITY_ENTITY_ID]" :to="editorControlsTeleportIds[activity.ACTIVITY_ENTITY_ID]">
                <div class="editor-controls">
                    <button type="button" v-if="ecUserProperty?.CLIENT_IS_STAFF === 'Yes' || config.workflowEditMode !== 'oppEdit'" @click="editActivity(activity.ACTIVITY_ENTITY_ID)" class="editor-action im-button" title="Edit Activity">
                        <i class="fa-solid fa-wrench"></i>
                    </button>
                    <button v-if="config.workflowEditMode !== 'oppEdit'" type="button" class="editor-action im-button" title="Remove activity from workflow"
                        @click="removeActivity(aIndex)"
                    ><i class="fa-solid fa-trash"></i></button>
                </div>
            </teleport>
        </div>
        <div class="action-area" v-if="controls?.['WORKFLOW_LIST_' + workflowModeUC]?.value && config.workflowEditMode !== 'oppEdit'">
            <control name="SAVE_WORKFLOW"></control>
        </div>
    </div>`,
    controls: [
        {
            name: 'WORKFLOW_LIST_ORG',
            type: 'dropdown',
            label: 'Workflows',
            itemsSource: 'configStore.workflowOptions()',
            fields: { text: 'text', value: 'value' },
            onchange: 'selectWorkflow'
        }, {
            name: 'WORKFLOW_LIST_OPP',
            type: 'dropdown',
            label: 'Workflows',
            itemsSource: 'configStore.workflowOptions(false, 1)',
            fields: { text: 'text', value: 'value' },
            onchange: 'selectWorkflow',
            logic: [
                {
                    record: 'self',
                    prop: 'label',
                    valueType: 'value',
                    value: 'Opportunity Workflow',
                    criteria: 'this.component.config.workflowEditMode === "oppEdit"'
                },
                {
                    record: 'self',
                    prop: 'helpText',
                    valueType: 'value',
                    value: 'Select the workflow you want to use for this opportunity. Additionally, you can link dynamic surveys here.',
                    criteria: 'this.component.config.workflowEditMode === "oppEdit"'
                }
            ]
        }, {
            name: 'WORKFLOW_TITLE',
            type: 'string',
            label: 'Title',
            placeholder: 'Give your workflow a recongnizable title.',
            cssClass: '',
            required: true
        }, {
            name: 'WORKFLOW_DESC',
            type: 'text',
            label: 'Description',
            placeholder: 'Describe your workflow for other coordinators to understand.',
            required: true
        }, {
            name: 'WORKFLOW_DEFAULT_STATUS',
            type: 'dropdown',
            label: 'Default Status',
            placeholder: 'Default Status',
            required: true,
            items: [
                { text: 'Complete', value: 3 },
                { text: 'In Progress', value: 2 },
                { text: 'Not Started', value: 1 },
                { text: 'Waiting', value: 4 },
                { text: 'Transferred', value: 5 }
            ],
            fields: { text: 'text', value: 'value' }
        }, {
            name: 'WORKFLOW_DISABLE_AUTO_STATUS_UPDATES',
            type: 'checkbox',
            label: 'Disable Automatic Status Updates',
        }, {
            name: 'WORKFLOW_DELETE',
            type: 'button',
            label: 'Delete Workflow',
            cssClass: '',
            onclick: 'deleteWorkflow',
            width: '200px'
        }, {
            name: 'ACTIVITY_LIST_ORG',
            type: 'dropdown',
            label: 'Activities',
            onchange: 'newActivity',
            placeholder: 'Select Activity',
            allowFiltering: true,
            itemsSource: 'configStore.activityOptions()',
            fields: {
                text: 'AE_TITLE',
                value: 'ACTIVITY_ENTITY_ID'
            }
        }, {
            name: 'ACTIVITY_LIST_OPP',
            type: 'dropdown',
            label: 'Activities',
            onchange: 'newActivity',
            placeholder: 'Select Activity',
            allowFiltering: true,
            itemsSource: 'configStore.activityOptions(1)',
            fields: {
                text: 'AE_TITLE',
                value: 'ACTIVITY_ENTITY_ID'
            }
        }, {
            name: 'ADD_USAGE_OPP',
            type: 'multiselect',
            label: 'Add DD.COMMON.ENTITY_OPPORTUNITY_PLURAL_CAPITALIZED',
            allowFiltering: true,
            fields: {
                text: 'OPP_TITLE',
                value: 'rowID'
            },
            itemsFilter: 'this.item.WORKFLOW_ID !== this.controls.WORKFLOW_LIST_OPP.value',
            itemsSource: [{
                source: ['ec_getGrid'],
                params: {
                    gridPsp: 'opp',
                    gridRecord: false,
                    gridFields: ['OPP_TITLE', 'OPP_WORKFLOW_ID']
                }
            }],
            helpText: 'Select new DD.COMMON.ENTITY_OPPORTUNITY_PLURAL to use this workflow.'
        }, {
            name: 'ADD_USAGE_ORG',
            type: 'multiselect',
            label: 'Add Organizations',
            allowFiltering: true,
            fields: {
                text: 'ORG_TITLE',
                value: 'rowID'
            },
            itemsFilter: 'this.item.WORKFLOW_ID !== this.controls.WORKFLOW_LIST_ORG.value',
            itemsSource: [{
                source: ['ec_getGrid'],
                params: {
                    gridPsp: 'org',
                    gridRecord: false,
                    gridFields: ['ORG_TITLE', 'ORG_KIND', 'WORKFLOW_ID']
                }
            }],
            helpText: 'Select new organizations to use this workflow.'
        }, {
            name: 'ADD_USAGE_BUTTON_ORG',
            type: 'button',
            label: 'Add',
            cssClass: 'e-info add-org-button',
            onclick: 'addWorkflowToOrg'
        }, {
            name: 'ADD_USAGE_BUTTON_OPP',
            type: 'button',
            label: 'Add',
            cssClass: 'e-info add-opp-button',
            onclick: 'addWorkflowToOpp'
        }, {
            name: 'USAGE_ORG',
            type: 'list',
            label: 'Organizations Using This Workflow',
            itemsFilter: 'this.item.WORKFLOW_ID.indexOf(this.controls.WORKFLOW_LIST_ORG.value) !== -1',
            itemsSource: [{
                source: ['ec_getGrid'],
                params: {
                    gridPsp: 'org',
                    gridRecord: false,
                    gridFields: ['ORG_TITLE', 'ORG_KIND', 'WORKFLOW_ID']
                }
            }],
            itemContentTemplate: `<div class="workflow-org-record">
                <div style="flex-grow: 1;">
                    <strong>{{data.ORG_TITLE}}</strong><br/>{{data.ORG_KIND}}<br/>
                </div>
                <control name="REMOVE_USAGE_ORG" :actionParams="data.rowID"></control>
            </div>`
        }, {
            name: 'USAGE_OPP',
            type: 'list',
            label: 'DD.COMMON.ENTITY_OPPORTUNITY_PLURAL_CAPITALIZED Using This Workflow',
            itemsFilter: 'this.item.OPP_WORKFLOW_ID.indexOf(this.controls.WORKFLOW_LIST_OPP.value) !== -1',
            itemsSource: [{
                source: ['ec_getGrid'],
                params: {
                    gridPsp: 'opp',
                    gridRecord: false,
                    gridFields: ['OPP_TITLE', 'OPP_WORKFLOW_ID']
                }
            }],
            itemContentTemplate: `<div class="workflow-opp-record">
                <div style="flex-grow: 1;">
                    <strong>{{data.OPP_TITLE}}</strong><br/>
                </div>
                <control name="REMOVE_USAGE_OPP" :actionParams="data.rowID"></control>
            </div>`
        }, {
            name: 'REMOVE_USAGE_ORG',
            type: 'button',
            label: 'Remove',
            cssClass: 'e-link',
            onclick: 'removeWorkflowFromOrg'
        }, {
            name: 'REMOVE_USAGE_OPP',
            type: 'button',
            label: 'Remove',
            cssClass: 'e-link',
            onclick: 'removeWorkflowFromOpp'
        }, {
            name: 'SAVE_WORKFLOW',
            type: 'button',
            label: 'Save Workflow',
            onclick: 'saveWorkflow',
            cssClass: 'save-workflow-button',
            display: (typeof VUE_PROJECT_NAME !== 'undefined' && VUE_PROJECT_NAME !== 'coordinator')
                || (typeof PROJECT_NAME !== 'undefined' && PROJECT_NAME !== 'coordinator'),
            logic: [
                {
                    record: 'self',
                    prop: 'disabled',
                    valueType: 'criteria',
                    criteria: '!this.component.workflowListValueSelected'
                }
            ]
        }, {
            name: 'NEW_WORKFLOW',
            type: 'button',
            label: 'New Workflow',
            onclick: 'newWorkflow',
            cssClass: '',
            logic: [
                {
                    record: 'self',
                    prop: 'display',
                    valueType: 'criteria',
                    criteria: '!this.component.workflowListValueSelected'
                }
            ]
        }, {
            name: 'ADD_ACTIVITY',
            type: 'button',
            label: 'Add',
            onclick: 'addActivity',
            cssClass: '',
            logic: [
                {
                    record: 'self',
                    prop: 'disabled',
                    valueType: 'criteria',
                    criteria: '!this.component.activityListValueSelected'
                }
            ],
            message: 'Select an activity and click add.'
        }, {
            name: 'NEW_ACTIVITY',
            type: 'button',
            label: 'New',
            onclick: 'selectNewActivity',
            cssClass: '',
            logic: [
                {
                    record: 'self',
                    prop: 'disabled',
                    valueType: 'criteria',
                    criteria: 'this.component.activityListValueSelected'
                }
            ],
            message: 'Select an activity and click add.'
        }, {
            name: 'TOGGLE_VISIBILITY',
            type: 'boolean',
            label: 'Make hidden items visible',
            inlineLabel: true,
            cssClass: '',
            default: true
        }
    ]
};

const workflowEditorCmp = {
    name: "workflowEditor",
    editorSettings: editorSettings,
    props: {
        mode: {
            default: 'org' /* possible values: 'org', 'opp' */
        },
    },
    data() {
        return {
            workflowSettings: {
                activities: {},
                ACTIVITY_ENTITY_ID: []
            },
            editorTabs: [],
            workflowOppSub: false,
            selectedTab: 0,
            // readyToTeleport: false,
            editorControlsTeleportIds: {},
            showTitleDesc: false,
            showActivityList: false,
            targetedActivity: false,
            targetedActivityId: false,
            // targetedActivityIndex: false,
            targetedActivityElemId: false,
            editorVisible: true,
            activitySubscription: false,
            user: false,
            prevWorkflowSettings: {},
            selectedWorkflow: '',
            isWfListReloading: false,
            activityWasRemoved: false,
            ddLoading: true
        };
    },
    computed: {
        workflowOpp() {
            if (this.config.workflowEditMode !== 'oppEdit') return {};
            if (!this.workflowOppSub) {
                let payload = {
                    source: 'sds_getProfileField',
                    params: {
                        recordPsp: 'opp',
                        recordId: this.oppId,
                        fields: [
                            'OPP_TITLE',
                            'OPP_WORKFLOW_ID',
                            'OPP_DYNAMIC_SURVEYS',
                            'OPP_DYNAMIC_TRAININGS',
                            'OPP_DYNAMIC_WAIVERS'
                        ],
                        params: { strictTypeResults: true }
                    },
                    updateFrequency: 60,
                };
                this.workflowOppSub = this.recordStore.subscribe(payload, this.cId, false, false);
            }
            return this.recordStore.records.opp[this.oppId];
        },
        workflowMode() {
            return this.config?.workflowMode || this.mode || 'org';
        },
        workflowModeUC() {
            return this.workflowMode.toUpperCase();
        },
        workflowListValueSelected() {
            return this.controls["WORKFLOW_LIST_" + this.workflowModeUC].value;
        },
        activityListValueSelected() {
            return this.controls["ACTIVITY_LIST_" + this.workflowModeUC].value;
        },
        computedWorkflowSettings() {
            let settings = library.cloneObj(this.workflowSettings);
            settings.activities = [];
            // Convert activity IDs to activities
            let workflowSelected = true;
            if (Array.isArray(settings.ACTIVITY_ENTITY_ID) && settings.ACTIVITY_ENTITY_ID.length) {
                settings.ACTIVITY_ENTITY_ID.forEach((activityId, activityIndex) => {
                    const activity = {
                        ACTIVITY_ENTITY_ID: activityId,
                        isComponentItem: true,
                        itemComponentId: 'activityStepPanel',
                        workflow: {
                            workflowId: this.selectedWorkflow,
                            workflowSelected
                        }
                    };
                    settings.activities.push(activity);
                });
            }
            return settings;
        },
        dynamicSurveys() {
            const parseStringToObject = (str) => {
                if (!str) return {};
                if (typeof str === 'object') return str;
                return Object.fromEntries(
                    str.split(',').map((pair) => {
                        const [key, value] = pair.split(': ');
                        return [key, value];
                    })
                );
            };
            return {
                OPP_DYNAMIC_SURVEYS: parseStringToObject(this.workflowOpp.OPP_DYNAMIC_SURVEYS),
                OPP_DYNAMIC_WAIVERS: parseStringToObject(this.workflowOpp.OPP_DYNAMIC_WAIVERS),
                OPP_DYNAMIC_TRAININGS: parseStringToObject(this.workflowOpp.OPP_DYNAMIC_TRAININGS),
            };
        }
    },
    methods: {
        async changeSelectedTab(tab) {
            await this.workflowOppSub.ready;
            this.editorControlsTeleportIds = {};
            this.selectedTab = tab;
            let workflowId = this.editorTabs?.[tab]?.workflowId;
            if (workflowId) {
                this.controls.WORKFLOW_LIST_OPP.value = workflowId;
                let itemData = this.controls.WORKFLOW_LIST_OPP.items.find((item) => item.value === workflowId);
                if (!itemData) return;
                this.selectWorkflow({
                    value: workflowId,
                    itemData
                });
            }
        },
        async getOppDetails() {
            if (!this.workflowOpp.OPP_WORKFLOW_ID) {
                await this.workflowOppSub.ready;
            }
            this.setEditorTabs();
            let workflowId = this.workflowOpp.OPP_WORKFLOW_ID;
            if (Array.isArray(workflowId) && workflowId.length) {
                this.controls.WORKFLOW_LIST_OPP.value = workflowId[0].toString();
                this.selectedTab = 3;
            }
        },
        getWorkflowDetails(workflowId) {
            let option = this.controls.WORKFLOW_LIST_OPP.items.find((item) => item.value === workflowId);
            return option?.text || '';
        },
        manualSave() {
            // called when Apply/Finish button clicked in Profile VMS-8955
            return this.saveWorkflow();
        },
        manualAfterSaveCheck(reloadTab) {
            return reloadTab ? 0 : 1; // prevent reloading tab
        },
        selectWorkflow(event) {
            this.resetWorkflowConfig();
            setTimeout(() => { // Timeout here to make sure ACTIVITY_ENTITY_ID registers as empty in the template
                let workflowId = event.value;
                this.selectedWorkflow = workflowId;
                if (!workflowId) return;
                this.activityWasRemoved = false;
                if (workflowId === -1) this.newWorkflow();
                if (!event.itemData) return;
                this.prevWorkflowSettings = library.cloneObj(event.itemData.details);
                library.mergeObj(this.workflowSettings, event.itemData.details);
            }, 100);
        },
        resetWorkflowConfig() {
            this.workflowSettings.WORKFLOW_ID = false;
            this.workflowSettings.WORKFLOW_TITLE = '';
            this.workflowSettings.WORKFLOW_DESC = '';
            this.workflowSettings.WORKFLOW_DEFAULT_STATUS = null;
            this.workflowSettings.WORKFLOW_DISABLE_AUTO_STATUS_UPDATES = false;
            this.workflowSettings.activities = {};
            this.workflowSettings.ACTIVITY_ENTITY_ID = [];
            this.prevWorkflowSettings = {};
        },
        newWorkflow() {
            if (this.config.workflowEditMode === 'oppEdit') return;
            if (this.controls['WORKFLOW_LIST_' + this.workflowModeUC].value !== -1) this.controls['WORKFLOW_LIST_' + this.workflowModeUC].value = -1;
            this.workflowSettings.WORKFLOW_ID = -1;
            this.workflowSettings.WORKFLOW_TITLE = 'A New Workflow';
            this.workflowSettings.WORKFLOW_DESC = 'Description';
            this.workflowSettings.WORKFLOW_DEFAULT_STATUS = 2;
            this.workflowSettings.WORKFLOW_DISABLE_AUTO_STATUS_UPDATES = false;
        },
        async saveWorkflow() {
            if (this.config.workflowEditMode === 'oppEdit') return;
            let deleteOption = 0;
            if (this.activityWasRemoved) {
                let deletedActivityDialog = {
                    name: 'deleted-activity-dialog-template',
                    template: `<div>
                                    <control name="deleteOption" :prop-config="controls.deleteOption" :value="deleteOption" @update:value="changeDeleteOption"></control>
                               </div>`,
                    data() {
                        return {
                            deleteOption: deleteOption,
                            controls: {
                                deleteOption: {
                                    name: 'deleteOption',
                                    id: 'deleteOption',
                                    type: 'radio_button_list',
                                    hideLabel: true,
                                    items: [
                                        {
                                            label: 'Leave existing Affiliations as-is',
                                            value: 0,
                                            helpText: 'Keep all current Vol/Activity affiliations exactly as they are. No records will be deleted or altered, even though the activity is no longer part of this Workflow.'
                                        },
                                        {
                                            label: 'Remove all existing Affiliations',
                                            value: 1,
                                            helpText: 'Delete the connection between the Volunteer and this activity for everyone currently on this Workflow, regardless of their progress or status.'
                                        },
                                        {
                                            label: 'Remove only unstarted Affiliations',
                                            value: 2,
                                            helpText: 'Only delete records where the Volunteer has not yet "transacted" (i.e., the Affiliation Status is still set to the Activity\'s default). This protects any Volunteers who have already started or completed the task.'
                                        },
                                    ]
                                }
                            }
                        };
                    },
                    methods: {
                        changeDeleteOption: (value) => {
                            deleteOption = value;
                        }
                    },
                    components: {
                        control: defineAsyncComponent(() => import('@common/control/control.vue'))
                    }
                };
                const deleteDialogResponse = await library.showDialog(
                    deletedActivityDialog,
                    true,
                    false,
                    { showAsAlert: true }
                );
                if (deleteDialogResponse.status === 'failure') {
                    // popup is closed, it means that save shoud be not triggered
                    return;
                }
            }
            let postData = library.cloneObj(this.workflowSettings);
            if (!this.workflowSettings.WORKFLOW_ID) return; // Exit out on unexpected UI behaviors
            if (this.workflowMode === 'opp') postData.WORKFLOW_TYPE = 1;
            if (
                !this.controls.WORKFLOW_TITLE.valid
                || !this.controls.WORKFLOW_DESC.valid
                || !this.controls.WORKFLOW_DEFAULT_STATUS.valid
            ) {
                await library.showDialog('Please fill in all required fields.');
                return;
            }
            delete postData.activities; // activities prop is for interface only
            library.showLoadingIndicator();
            let response;
            if (this.workflowSettings.WORKFLOW_ID === -1) {
                delete postData.WORKFLOW_ID;
                response = await this.sDs.post('sds_createWorkflow', postData);
            } else {
                response = await this.sDs.post('sds_updateWorkflow', postData.WORKFLOW_ID, postData, {
                    aeDeleteOption: deleteOption
                });
            }
            if (response && response.status === 'success') {
                this.activityWasRemoved = false;
                library.showToast('Workflow saved successfully!');
                if (this.workflowSettings.WORKFLOW_ID === -1) {
                    this.selectedWorkflow = response.data;
                    this.reloadWorkflowList();
                }
                if (
                    this.workflowSettings.WORKFLOW_ID !== -1
                    && !library.objIsEmpty(this.prevWorkflowSettings)
                    && Array.isArray(this.prevWorkflowSettings.ACTIVITY_ENTITY_ID)
                    && Array.isArray(this.workflowSettings.ACTIVITY_ENTITY_ID)
                ) {
                    let newActivityIsAdded = this.workflowSettings.ACTIVITY_ENTITY_ID.some((tmpEl) => this.prevWorkflowSettings.ACTIVITY_ENTITY_ID.indexOf(tmpEl) === -1);
                    if (newActivityIsAdded) {
                        await library.showDialog('Do you want to create affiliations for the new activity for already affiliated volunteers?', () => {
                            if (this.workflowMode === 'org') {
                                this.sDs.post('sds_affiliateVolWithOrgActivities', false, false, false, postData.WORKFLOW_ID);
                            } else {
                                this.sDs.post('sds_affiliateVolWithOppActivities', false, false, false, postData.WORKFLOW_ID);
                            }
                        }, false, { name: 'confirmActivityAffiliation' });
                    }
                }
            } else library.showToast('Failure: We could not save your workflow.  Please try again.');
            library.hideLoadingIndicator();
        },
        selectNewActivity() {
            this.controls["ACTIVITY_LIST_" + this.workflowModeUC].value = -1;
        },
        async newActivity(event) {
            if (event.value === -1) {
                // this.targetedActivityId = -1;
                let response = await this.editActivity(-1); // Creates a new activity
                this.controls["ACTIVITY_LIST_" + this.workflowModeUC].value = null;
                if (typeof response !== 'object') {
                    library.showToast('Action cancelled');
                    return;
                }
                let activityId = response.data;
                this.workflowSettings.ACTIVITY_ENTITY_ID.push(activityId);
                let wfType = this.workflowMode === 'org' ? 0 : 1;
                await configStore.activityOptions(wfType, true);
                // wait for details to load, and get activity title for message below
                library.showToast('New activity created, ID:' + activityId);
            }
        },
        selectActivity(item) {
            // TODO Decide if we can remove this, selecting an activity is now done using the wrench
            // if (item.itemIndex === this.targetedActivityIndex) return;
            // // this.readyToTeleport = false;
            // this.targetedActivity = false;
            // this.targetedActivityId = false;
            // this.targetedActivityIndex = false;
            // // this.targetedActivityElemId = '[id="' + item.elemId + '"]';
            // setTimeout(() => {
            //     // this.readyToTeleport = true;
            //     this.targetedActivity = item;
            //     this.targetedActivityId = item.ACTIVITY_ENTITY_ID;
            //     this.targetedActivityIndex = item.itemIndex;
            // }, 100);
        },
        updateOrder(event) {
            let tmpActivityIds = [];
            let currActivites = this.workflowSettings.ACTIVITY_ENTITY_ID;
            for (let index in event.itemOrder) {
                if (index === 'length') continue;
                let actIndex = event.itemOrder[index];
                tmpActivityIds[index] = currActivites[actIndex];
            }
            this.workflowSettings.ACTIVITY_ENTITY_ID = tmpActivityIds;
        },
        addActivity() {
            if (!this.controls["ACTIVITY_LIST_" + this.workflowModeUC].value) return;
            this.workflowSettings.ACTIVITY_ENTITY_ID.push(this.controls["ACTIVITY_LIST_" + this.workflowModeUC].value);
            this.controls["ACTIVITY_LIST_" + this.workflowModeUC].value = null;
        },
        async editActivity(activityId) {
            if (!activityId) return false;
            let relatedRecords = {
                'wf': this.workflowSettings
            };
            return actionStore.openProfile({ recordPsp: 'ae', recordId: activityId, relatedRecords: relatedRecords });
        },
        removeActivity(activityIndex) {
            this.workflowSettings.ACTIVITY_ENTITY_ID.splice(activityIndex, 1);
            if (this.workflowSettings.WORKFLOW_ID !== -1) this.activityWasRemoved = true;
        },
        async removeWorkflowFromOrg(_event, control) {
            let orgId = control.actionParams;
            let wfId = this.controls.WORKFLOW_LIST_ORG.value;
            let WORKFLOW_ID = library.cloneObj(this.recordStore.records.org[orgId].WORKFLOW_ID);
            WORKFLOW_ID.splice(WORKFLOW_ID.indexOf(wfId), 1);
            let response = await this.sDs.post('sds_updateProfileField', 'org', orgId, { WORKFLOW_ID });
            if (response.status === 'success') {
                library.showDialog('Success! The workflow has been removed from this organizaton.', false, false, { showAsAlert: true });
                this.recordStore.records.org[orgId].WORKFLOW_ID = WORKFLOW_ID;
                this.recordStore.setModified({ org: [orgId] });
            } else if (response.status === 'failure') {
                library.showDialog('Failure. Removing the workflow was not successful.', false, false, { showAsAlert: true });
            }
        },
        async removeWorkflowFromOpp(_event, control) {
            let oppId = control.actionParams;
            let wfId = this.controls.WORKFLOW_LIST_OPP.value;
            let OPP_WORKFLOW_ID = library.cloneObj(this.recordStore.records.opp[oppId].OPP_WORKFLOW_ID);
            OPP_WORKFLOW_ID.splice(OPP_WORKFLOW_ID.indexOf(wfId), 1);
            let response = await this.sDs.post('sds_updateProfileField', 'opp', oppId, { OPP_WORKFLOW_ID });
            if (response.status === 'success') {
                let oppEntityDD = await ddStore.getAlias('COMMON', 'ENTITY_OPPORTUNITY');
                library.showDialog('Success! The workflow has been removed from this ' + oppEntityDD + '.', false, false, { showAsAlert: true });
                this.recordStore.records.opp[oppId].OPP_WORKFLOW_ID = OPP_WORKFLOW_ID;
                this.recordStore.setModified({ opp: [oppId] });
            } else if (response.status === 'failure') {
                library.showDialog('Failure. Removing the workflow was not successful.', false, false, { showAsAlert: true });
            }
        },
        async addWorkflowToOrg() {
            let orgIds = this.controls.ADD_USAGE_ORG.value;
            if (!orgIds) {
                library.showDialog('Select an organization to add.', false, false, { showAsAlert: true });
                return;
            }
            if (!Array.isArray(orgIds)) orgIds = [orgIds];
            let wfId = this.controls.WORKFLOW_LIST_ORG.value;
            let promises = [];
            let updatedOrgIds = [];
            orgIds.forEach((orgId) => {
                // Orgs should already be in the recordStore from ADD_USAGE_ORG control
                let WORKFLOW_ID = library.cloneObj(this.recordStore.records.org[orgId].WORKFLOW_ID);
                if (WORKFLOW_ID.indexOf(wfId) === -1) {
                    WORKFLOW_ID.push(wfId);
                    promises.push(this.sDs.post('sds_updateProfileField', 'org', orgId, { WORKFLOW_ID }));
                    updatedOrgIds.push(orgId); // For response handling
                }
            });
            let responses = await library.promiseAllSettled(promises);
            let failedOrgs = [];
            let successOrgs = [];
            responses.forEach((res, index) => {
                let updatedOrg = this.recordStore.records.org[updatedOrgIds[index]];
                if (res.value.status === 'success') {
                    updatedOrg.WORKFLOW_ID.push(wfId);
                    successOrgs.push(updatedOrg.ORG_TITLE);
                }
                else failedOrgs.push(updatedOrg.ORG_TITLE);
            });
            if (updatedOrgIds.length) this.recordStore.setModified({ org: updatedOrgIds });
            if (successOrgs.length) library.showDialog('<strong>Success!</strong><br/>The selected organization(s) are now using this workflow: ' + successOrgs.join(', ') + '<br/><br/>You should now see them in the list below.', false, false, { showAsAlert: true });
            if (failedOrgs.length) library.showDialog('<strong>Failure</strong><br/>We were not able to set the workflow for the following organizations: ' + failedOrgs.join(', '), false, false, { showAsAlert: true });
        },
        async addWorkflowToOpp() {
            let oppIds = this.controls.ADD_USAGE_OPP.value;
            if (!oppIds) {
                let oppEntityDD = await ddStore.getAlias('COMMON', 'ENTITY_OPPORTUNITY');
                library.showDialog('Select an ' + oppEntityDD + ' to add.', false, false, { showAsAlert: true });
                return;
            }
            if (!Array.isArray(oppIds)) oppIds = [oppIds];
            let wfId = this.controls.WORKFLOW_LIST_OPP.value;
            if (wfId === -1) {
                let oppEntityDD = await ddStore.getAlias('COMMON', 'ENTITY_OPPORTUNITY');
                library.showDialog('You need to save workflow before adding ' + oppEntityDD, false, false, { showAsAlert: true });
                return;
            }
            let promises = [];
            let updatedOppIds = [];
            oppIds.forEach((oppId) => {
                // Opps should already be in the recordStore from ADD_USAGE_OPP control
                let OPP_WORKFLOW_ID = library.cloneObj(this.recordStore.records.opp[oppId].OPP_WORKFLOW_ID);
                if (OPP_WORKFLOW_ID.indexOf(wfId) === -1) {
                    OPP_WORKFLOW_ID.push(wfId);
                    promises.push(this.sDs.post('sds_updateProfileField', 'opp', oppId, { OPP_WORKFLOW_ID }));
                    updatedOppIds.push(oppId); // For response handling
                }
            });
            let responses = await library.promiseAllSettled(promises);
            let failedOpps = [];
            let successOpps = [];
            responses.forEach((res, index) => {
                let updatedOpp = this.recordStore.records.opp[updatedOppIds[index]];
                if (res.value.status === 'success') {
                    updatedOpp.OPP_WORKFLOW_ID.push(wfId);
                    successOpps.push(updatedOpp.OPP_TITLE);
                }
                else failedOpps.push(updatedOpp.OPP_TITLE);
            });
            if (updatedOppIds.length) this.recordStore.setModified({ opp: updatedOppIds });
            if (successOpps.length) {
                let oppEntityDD = await ddStore.getAlias('COMMON', 'ENTITY_OPPORTUNITY_OR_PLURAL');
                library.showDialog('<strong>Success!</strong><br/>The selected ' + oppEntityDD + ' are now using this workflow: ' + successOpps.join(', ') + '<br/><br/>You should now see them in the list below.', false, false, { showAsAlert: true });
            }
            if (failedOpps.length) {
                let oppEntityDD = await ddStore.getAlias('COMMON', 'ENTITY_OPPORTUNITY_OR_PLURAL');
                library.showDialog('<strong>Failure</strong><br/>We were not able to set the workflow for the following ' + oppEntityDD + ': ' + failedOpps.join(', '), false, false, { showAsAlert: true });
            }
        },
        async reloadWorkflowList() {
            let wfType = this.workflowMode === 'org' ? 0 : 1;
            await configStore.workflowOptions(true, wfType);
            this.isWfListReloading = true;
            this.$nextTick(() => {
                this.isWfListReloading = false;
            });
        },
        deleteWorkflow() {
            if (this.selectedWorkflow > 0) {
                library.showDialog('Are you sure you want to delete this workflow?', async () => {
                    let response = await this.sDs.post('sds_deleteWorkflow', this.selectedWorkflow);
                    if (response && response.status === 'success') {
                        this.selectedWorkflow = null;
                        this.reloadWorkflowList();
                        library.showToast('Workflow deleted successfully!');
                    } else {
                        let message = Array.isArray(response.error) ? response.error.join('<br/>') : 'Workflow not deleted';
                        library.showToast(message, { type: 'warning' });
                    }
                }, false);
            }
        },
        async openDynamicSurveysDialog(event) {
            let aeId = event.activityId;
            let actionId = event.actionId;
            await this.workflowOppSub?.ready;
            let oppId = this.oppId;
            let orgId = this.orgId;
            let records = this.recordStore.records;
            let dynamicSurveys = this.dynamicSurveys;
            const surveyControls = {};
            const defaultSurveyControls = {
                OPP_DYNAMIC_SURVEYS: {
                    name: 'OPP_DYNAMIC_SURVEYS',
                    type: 'dropdown',
                    label: 'Opp Specific Survey',
                    placeholder: 'Opp Specific Survey',
                    cssClass: '',
                    multiselect: false,
                    itemsSource: 'configStore.allSurveyOptions()',
                    itemsFilter: 'this.item.pId', // Only show surveys, not parent items
                    onchange: 'setControlValues',
                    fields: {
                        text: 'text',
                        value: 'value',
                        hasChildren: 'hasChild',
                        parentValue: 'pId',
                    },
                    default: '',
                    removeOnDestroy: true,
                    // Note Logic is handled in the dialog template
                },
                OPP_DYNAMIC_WAIVERS: {
                    name: 'OPP_DYNAMIC_WAIVERS',
                    type: 'dropdown',
                    label: 'Opp Specific Waiver',
                    placeholder: 'Opp Specific Waiver',
                    cssClass: '',
                    multiselect: false,
                    itemsSource: 'configStore.allWaiverOptions()',
                    onchange: 'setControlValues',
                    fields: {
                        text: 'text',
                        value: 'value',
                        hasChildren: 'hasChild',
                        parentValue: 'pId',
                    },
                    default: '',
                    removeOnDestroy: true,
                    // Note Logic is handled in the dialog template
                },
                OPP_DYNAMIC_TRAININGS: {
                    name: 'OPP_DYNAMIC_TRAININGS',
                    type: 'dropdown',
                    label: 'Opp Specific Training',
                    placeholder: 'Opp Specific Training',
                    cssClass: '',
                    multiselect: false,
                    itemsSource: 'configStore.allTrainingOptions()',
                    onchange: 'setControlValues',
                    fields: {
                        text: 'text',
                        value: 'value',
                        hasChildren: 'hasChild',
                        parentValue: 'pId',
                    },
                    default: '',
                    removeOnDestroy: true,
                    // Note Logic is handled in the dialog template
                }
            };
            const controlValues = {};
            // Get Available Dynamic Surveys
            let storedActivities = workflowStore()?.editorWorkflows?.[this.selectedWorkflow];
            Object.values(storedActivities).forEach((storedActivity) => {
                if (Array.isArray(storedActivity?.actions)) {
                    let activityId = storedActivity.ACTIVITY_ENTITY_ID;
                    storedActivity.actions.forEach((action) => {
                        if (!action.useDynamicSurvey || library.objIsEmpty(action.dynamicSurveys)) return;
                        for (let surveyKey in action.dynamicSurveys) {
                            let tmpControl;
                            let defaultKey;
                            if (!surveyKey.includes(actionId)) continue; // Only get surveys for this action
                            if (surveyKey.includes('SURVEY')) defaultKey = 'OPP_DYNAMIC_SURVEYS';
                            else if (surveyKey.includes('WAIVER')) defaultKey = 'OPP_DYNAMIC_WAIVERS';
                            else if (surveyKey.includes('TRAINING')) defaultKey = 'OPP_DYNAMIC_TRAININGS';
                            if (!defaultKey) continue;
                            tmpControl = library.cloneObj(defaultSurveyControls[defaultKey]);
                            tmpControl.name = surveyKey;
                            tmpControl.value = dynamicSurveys[defaultKey]?.[actionId];
                            surveyControls[surveyKey] = tmpControl;
                        }
                    });
                }
            });
            let dialogComponent = {
                name: 'dynamicSurveyEditor',
                data() {
                    return {
                        oppId,
                        orgId,
                        aeId,
                        surveyControls
                    };
                },
                template: `<div class="dynamic-surveys-dialog">
                    <div class="dynamic-surveys-dialog-inner">
                        <div class="alert alert-primary" style="display: flex;">
                            <div style="margin-right: 10px;"><i class="fa fa-info-circle">{{}}</i></div>
                            <div style="flex-grow: 9;">
                                <strong>Note:</strong> Dynamic surveys are used in activities where each opportunity requires a different survey for the same activity.
                            </div>
                        </div>
                        <div v-for="iControl in surveyControls">
                            <control :prop-config="iControl" :name="iControl.name" :actionParams="iControl.name"></control>
                        </div>
                    </div>
                </div>`,
                computed: {
                    records() {
                        return records;
                    },
                    computedOpp() {
                        return this.records.opp[this.oppId];
                    }
                },
                methods: {
                    setControlValues(event, controlName) {
                        let value = event.value;
                        // handle array values
                        if (Array.isArray(value)) value = value[0];
                        if (!value) value = "";
                        controlValues[controlName] = value;
                    }
                },
                components: {
                    control: defineAsyncComponent(() => import('@common/control/control.vue'))
                }
            };
            let dialogOptions = {
                title: 'Opp Specific Surveys',
                isComponentDialog: true
            };
            let saveDynamicSurvey = async () => {
                // Append the survey to the OPP_DYNAMIC_SURVEYS field
                let tmpFields = {};
                let keys = Object.keys(controlValues);
                keys.forEach((key) => {
                    let tmpKey = key.substring(0, key.lastIndexOf('_'));
                    tmpFields[tmpKey] = dynamicSurveys[tmpKey] || {};
                    tmpFields[tmpKey][actionId] = controlValues[key];
                });
                await actionStore.updateProfileFields({ psp: 'opp', recordId: oppId, fields: tmpFields });
            };
            library.showDialog(dialogComponent, saveDynamicSurvey, false, dialogOptions);
        },
        async onLoad() {
            library.showLoadingIndicator('body');
            await ddStore.preloadGroup('COMMON');
            await ddStore.preloadGroup('ACTIVITY_ENTITY');
            this.ddLoading = false;
            library.hideLoadingIndicator('body');
            if (this.config.workflowViewType === 'oppEdit') {
                this.getOppDetails();
            }
            else {
                this.setEditorTabs();
                setTimeout(() => {
                    let currentValue = this?.controls?.['WORKFLOW_LIST_' + this.workflowModeUC]?.value;
                    if (currentValue) {
                        let itemData = this.controls['WORKFLOW_LIST_' + this.workflowModeUC].items.find((item) => item.value === currentValue);
                        this.selectWorkflow({
                            value: currentValue,
                            itemData
                        });
                    }
                }, 1000);
            }
        },
        onStepReady(item) {
            // TODO if all are ready
            this.editorControlsTeleportIds[item.ACTIVITY_ENTITY_ID] = '[id="editor_teleported_controls_' + item.ACTIVITY_ENTITY_ID + '"]';
        },
        setEditorTabs() {
            // Note this should only set once, when the opp
            let editorTabs = [];
            editorTabs = [
                {
                    title: 'Activities List',
                    useVueSlot: true,
                }, {
                    title: 'General',
                    useVueSlot: true,
                }, {
                    title: 'Usage',
                    useVueSlot: true,
                }
            ];
            if (this.config.workflowViewType === 'oppEdit') {
                const tabTemplate = {
                    title: 'Workflow Configuration',
                    useVueSlot: true,
                    show: true,
                    oppWorkflowIndex: 3,
                };
                if (this.oppId && Array.isArray(this.workflowOpp.OPP_WORKFLOW_ID)) {
                    let tmpIndex = 3;
                    editorTabs[0].show = false;
                    editorTabs[1].show = false;
                    editorTabs[2].show = false;
                    this.workflowOpp.OPP_WORKFLOW_ID.forEach((wfId, idx) => {
                        let tmpTab = library.cloneObj(tabTemplate);
                        let workflowTitle = this.getWorkflowDetails(wfId);
                        tmpTab.title = workflowTitle + ' Workflow Configuration';
                        tmpTab.oppWorkflowIndex = tmpIndex;
                        tmpTab.workflowId = wfId;
                        tmpTab.slotTemplateId = 'tabList_item_' + tmpIndex;
                        tmpIndex++;
                        editorTabs.push(tmpTab);
                    });
                }
            }
            this.editorTabs = editorTabs;
        },
    },
    components: {
        profile: defineAsyncComponent(() => import('@common/profile.vue')),
        tabList: defineAsyncComponent(() => import('@common/tabList/tabList.vue'))
    }
};
componentStore.initComponent(workflowEditorCmp, workflowEditorConfig);

export default workflowEditorCmp;
</script>

<style scoped>
.top-actions-panel {
    display: flex;
    justify-content: flex-end;
}
#activity-actions-panel {
    position: absolute;
    right: 0;
    top: 0;
    width: 140px;
    padding: 5px;
    overflow: visible;
    background: rgba(255, 255, 255, 0.45);
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: center;
}
#activity-actions-panel > .control.e-control {
    margin-bottom: 5px;
}
.action-area {
    margin: 20px 0;
    display: flex;
    justify-content: flex-end;
}
.action-area > div + div {
    margin-left: 10px;
}
.save-workflow-button {
    flex-shrink: 0
}
.workflow-message {
    font-size: 16px;
    background: #fff3c5;
    border: 1px solid #f5ce45;
    color: #4f3a05;
    padding: 10px;
    text-align: center;
    border-radius: 5px;
}
.inline-link-button {
    display: inline-block;
}
.inline-link-button :deep(button) {
    background: rgba(255, 255, 255, 0.7);
    padding: 4px;
    border-radius: 5px;
    box-shadow: none;
    text-transform: lowercase;
    text-decoration: underline;
}
.edit-icon-button {
    background: #E7F8FF7F;
    padding: 4px;
    border-radius: 5px;
    border: 0 none;
}
.edit-icon-button:hover {
    background: rgba(231, 248, 255, 1);
}
.e-icons.e-edit:before {
    content: '\e891' !important;
}
:deep(.workflow-org-record) {
    padding: 10px;
    border: 1px solid #f0f0f0;
    border-bottom: 1px solid white;
    background: rgba(0, 0, 0, 0.05);
    display: flex;
}
:deep(.workflow-org-record button span) {
    text-transform: lowercase
}
:deep(.add-workflow-controls) {
    display: flex;
    align-items: flex-end;
    margin-bottom: 15px;
}
:deep(.add-org-dropdown) {
    margin-right: 15px;
}
:deep(.add-org-button) {
    padding-left: 15px;
    padding-right: 15px;
}
:deep(.disable-auto-status-updates) {
    text-align: left;
    margin-top: 10px;
}
:deep(.item-list-container:has(.hide-other-steps) .list-item:not(.hide-other-steps)) {
    display: block !important;
}
:deep(.list-component-item:hover) {
    background: transparent;
}
.opp-edit-mode #activity-actions-panel {
    background: white;
    width: 340px;
    top: 8px;
    bottom: 10px;
    right: 6px;
    border: 1px solid #ddd;
    border-left: 6px solid #ddd;
    border-radius: 4px;
    height: auto;
    justify-content: flex-start;
    padding-top: 0;
    padding-left: 0;
}
/*:deep(.opp-edit-mode .activity-step-panel.editor-enabled) {
    min-height: 140px;
}*/
/* Styles from activity step panel */
:deep(.editor-action) {
    border: 0 none;
    transition: background .1s ease-in;
    background: rgba(255, 255, 255, .20);
    border-radius: 15px;
    color: #2b2b86;
    padding: 4px 12px;
    margin: 0 3px 0 0;
    text-decoration: underline;
}
:deep(.editor-action:hover) {
    background: rgba(0, 0, 0, .08);
    transition: all .1s ease-in-out;
}
:deep(.editor-action:disabled) {
    cursor: not-allowed;
    opacity: 60%;
}
.editor-controls {
    padding:8px;
    border-radius: 16px;
    display: inline-block;
    background: rgb(15 30 41 / 0.1);
}
/*.editor-controls .configure-dynamic-surveys {
    font-size: 16px;
    background: white;
    text-decoration: none;
    font-weight: bold;
}
.editor-controls .configure-dynamic-surveys i.fa-triangle-exclamation {
    display: none;
}
.editor-controls .warning-action {
    border: 1px solid #ff3e33;
}
.editor-controls .configure-dynamic-surveys.warning-action > i {
    color: #ff3e33;
    animation: vibrate 30s infinite ease-in-out;
    align-self: start;
    display: inherit;
}*/
:deep(.workflow-editor:not(.opp-edit-mode) .activity-step-panel.editor-enabled .dynamic-surveys-button) {
    display: none;
}
:deep(.workflow-editor:not(.opp-edit-mode) .activity-step-panel.editor-enabled .dynamic-surveys-indicator) {
    display: block;
}
@keyframes vibrate {
    0%, 96.9% { transform: rotate(0deg) translateX(0); }
    97.0% { transform: rotate(-3deg) translateX(-2px); }
    97.1% { transform: rotate(3deg)  translateX(2px); }
    97.2% { transform: rotate(-3deg) translateX(-2px); }
    97.3% { transform: rotate(3deg)  translateX(2px); }
    97.4% { transform: rotate(-2.5deg) translateX(-1.8px); }
    97.5% { transform: rotate(2.5deg)  translateX(1.8px); }
    97.6% { transform: rotate(-2.5deg) translateX(-1.8px); }
    97.7% { transform: rotate(2.5deg)  translateX(1.8px); }
    97.8% { transform: rotate(-2deg) translateX(-1.5px); }
    97.9% { transform: rotate(2deg)  translateX(1.5px); }
    98.0% { transform: rotate(-2deg) translateX(-1.5px); }
    98.1% { transform: rotate(2deg)  translateX(1.5px); }
    98.2% { transform: rotate(-1.5deg) translateX(-1px); }
    98.3% { transform: rotate(1.5deg)  translateX(1px); }
    98.4% { transform: rotate(-1.5deg) translateX(-1px); }
    98.5% { transform: rotate(1.5deg)  translateX(1px); }
    98.6% { transform: rotate(-1deg) translateX(-0.8px); }
    98.7% { transform: rotate(1deg)  translateX(0.8px); }
    98.8% { transform: rotate(-0.5deg) translateX(-0.5px); }
    98.9% { transform: rotate(0.5deg)  translateX(0.5px); }
    99%, 100% { transform: rotate(0deg) translateX(0); }
}
</style>
